"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useLayoutEffect, useRef, useState } from "react";
import { ArrowLeftIcon, CheckIcon, ChevronDoubleLeftIcon, ChevronDownIcon, EllipsisHorizontalIcon, LinkIcon, MagnifyingGlassIcon, ShareIcon } from "@heroicons/react/24/outline";
import { CompactMenu, type CompactMenuItem } from "@/components/compact-menu";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/components/auth/Auth";
import type { SetActiveContentTab } from "@/components/brain/ContentTabContext";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { getDirectMessageProfileHref } from "@/helpers/waves/direct-message-profile.helpers";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
import { MyStreamWaveTab } from "@/types/waves.types";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import WaveDescriptionPopover from "@/components/waves/header/WaveDescriptionPopover";
import WavePicture from "../../../waves/WavePicture";
import MyStreamActionTooltip from "../MyStreamActionTooltip";
import { useSidebarState } from "../../../../hooks/useSidebarState";
const TRUNCATION_EPSILON_PX = 1;
export interface MyStreamWaveTabsHeaderActionContext {
  readonly activeContentTab: MyStreamWaveTab;
  readonly isCompact: boolean;
  readonly tooltipId: string;
}
interface MyStreamWaveTabsHeaderProps {
  readonly wave: ApiWave;
  readonly activeContentTab: MyStreamWaveTab;
  readonly setActiveContentTab: SetActiveContentTab;
  readonly onSelectCuration: (curationId: string | null) => void;
  readonly isCompact: boolean;
  readonly showBackButton: boolean;
  readonly headerActionsTooltipId: string;
  readonly headerClassName: string;
  readonly actionsClassName: string;
  readonly rightSidebarButtonBackgroundClassName?: string | undefined;
  readonly renderLeadingActions?: ((context: MyStreamWaveTabsHeaderActionContext) => React.ReactNode) | undefined;
  readonly renderOverflowMenuItems?: ((context: MyStreamWaveTabsHeaderActionContext) => readonly CompactMenuItem[]) | undefined;
}
export default function MyStreamWaveTabsHeader(t0) {
  const $ = _c(92);
  const {
    wave,
    activeContentTab,
    setActiveContentTab,
    onSelectCuration,
    isCompact,
    showBackButton,
    headerActionsTooltipId,
    headerClassName,
    actionsClassName,
    rightSidebarButtonBackgroundClassName: t1,
    renderLeadingActions,
    renderOverflowMenuItems
  } = t0;
  const rightSidebarButtonBackgroundClassName = t1 === undefined ? "tw-bg-iron-800" : t1;
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const descriptionPreviewRef = useRef(null);
  const [isDescriptionPreviewTruncated, setIsDescriptionPreviewTruncated] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const t2 = activeProfileProxy?.created_by;
  let t3;
  if ($[0] !== connectedProfile || $[1] !== isDirectMessage || $[2] !== t2 || $[3] !== wave.name) {
    t3 = getDirectMessageProfileHref({
      isDirectMessage,
      identity: wave.name,
      connectedProfile,
      activeProfileProxyCreatedBy: t2
    });
    $[0] = connectedProfile;
    $[1] = isDirectMessage;
    $[2] = t2;
    $[3] = wave.name;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const directMessageProfileHref = t3;
  let t4;
  if ($[5] !== wave.contributors_overview) {
    t4 = wave.contributors_overview.map(_temp);
    $[5] = wave.contributors_overview;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const wavePictureContributors = t4;
  const showShareAction = !isDirectMessage;
  let t5;
  if ($[7] !== wave) {
    t5 = getWaveDescriptionPreviewText(wave);
    $[7] = wave;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const previewText = t5;
  const showDescriptionPreview = showShareAction && !!previewText;
  let t6;
  if ($[9] !== isDirectMessage || $[10] !== wave.id || $[11] !== wave.name) {
    t6 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[9] = isDirectMessage;
    $[10] = wave.id;
    $[11] = wave.name;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t6);
  let t7;
  if ($[13] !== isApp || $[14] !== isDirectMessage || $[15] !== router || $[16] !== searchParams) {
    t7 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      params.delete("curation");
      const basePath = getWaveHomeRoute({
        isDirectMessage,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[13] = isApp;
    $[14] = isDirectMessage;
    $[15] = router;
    $[16] = searchParams;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleMobileBack = t7;
  let t8;
  if ($[18] !== onSelectCuration || $[19] !== pathname || $[20] !== router || $[21] !== searchParams || $[22] !== setActiveContentTab || $[23] !== wave.id || $[24] !== waveChatScroll) {
    t8 = serialNo => {
      onSelectCuration(null);
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.delete("curation");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[18] = onSelectCuration;
    $[19] = pathname;
    $[20] = router;
    $[21] = searchParams;
    $[22] = setActiveContentTab;
    $[23] = wave.id;
    $[24] = waveChatScroll;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const handleSearchSelect = t8;
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  const rightSidebarActionLabel = isRightSidebarOpen ? "Hide right sidebar" : "Show right sidebar";
  const rightSidebarCompactLabel = isRightSidebarOpen ? "Hide details" : "Wave details";
  let t9;
  if ($[26] !== waveLinkActionFeedbackState || $[27] !== waveLinkActionMode) {
    t9 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[26] = waveLinkActionFeedbackState;
    $[27] = waveLinkActionMode;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const renderWaveLinkActionIcon = t9;
  const actionContext = {
    activeContentTab,
    isCompact,
    tooltipId: headerActionsTooltipId
  };
  const externalOverflowMenuItems = isCompact ? renderOverflowMenuItems?.(actionContext) ?? [] : [];
  const headerOverflowMenuItems = isCompact ? [...(showShareAction ? [{
    id: "wave-link",
    label: waveLinkActionLabel,
    icon: renderWaveLinkActionIcon(),
    onSelect: handleWaveLinkActionClick
  }] : []), {
    id: "wave-details",
    label: rightSidebarCompactLabel,
    icon: <ChevronDoubleLeftIcon strokeWidth={2} className={`tw-h-4 tw-w-4 tw-flex-shrink-0 tw-transition ${isRightSidebarOpen ? "tw-rotate-180" : "tw-rotate-0"}`} />,
    onSelect: toggleRightSidebar
  }] : [];
  const compactMenuItems = [...externalOverflowMenuItems, ...headerOverflowMenuItems];
  let t10;
  if ($[29] !== isCompact || $[30] !== showDescriptionPreview) {
    t10 = () => {
      if (!showDescriptionPreview || isCompact) {
        const frameId = globalThis.requestAnimationFrame(() => {
          setIsDescriptionPreviewTruncated(false);
        });
        return () => {
          globalThis.cancelAnimationFrame(frameId);
        };
      }
      const previewElement = descriptionPreviewRef.current;
      if (!previewElement) {
        return;
      }
      const updateTruncationState = () => {
        setIsDescriptionPreviewTruncated(previewElement.scrollWidth > previewElement.clientWidth + TRUNCATION_EPSILON_PX);
      };
      const frameId_0 = globalThis.requestAnimationFrame(updateTruncationState);
      if (typeof ResizeObserver === "undefined") {
        globalThis.addEventListener("resize", updateTruncationState);
        return () => {
          globalThis.cancelAnimationFrame(frameId_0);
          globalThis.removeEventListener("resize", updateTruncationState);
        };
      }
      const observer = new ResizeObserver(updateTruncationState);
      observer.observe(previewElement);
      if (previewElement.parentElement) {
        observer.observe(previewElement.parentElement);
      }
      return () => {
        globalThis.cancelAnimationFrame(frameId_0);
        observer.disconnect();
      };
    };
    $[29] = isCompact;
    $[30] = showDescriptionPreview;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== isCompact || $[33] !== previewText || $[34] !== showDescriptionPreview) {
    t11 = [isCompact, previewText, showDescriptionPreview];
    $[32] = isCompact;
    $[33] = previewText;
    $[34] = showDescriptionPreview;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  useLayoutEffect(t10, t11);
  let t12;
  if ($[36] !== handleMobileBack || $[37] !== showBackButton) {
    t12 = showBackButton && <button type="button" onClick={handleMobileBack} className="tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-1.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50 sm:-tw-ml-2.5 sm:tw-px-2.5" aria-label="Go back"><ArrowLeftIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 sm:tw-h-6 sm:tw-w-6" /></button>;
    $[36] = handleMobileBack;
    $[37] = showBackButton;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== directMessageProfileHref || $[40] !== isCompact || $[41] !== isDescriptionPreviewTruncated || $[42] !== previewText || $[43] !== showDescriptionPreview || $[44] !== wave || $[45] !== wavePictureContributors) {
    t13 = directMessageProfileHref ? <Link href={directMessageProfileHref} aria-label={`View ${wave.name}'s profile`} className="tw-flex tw-min-w-0 tw-items-center tw-gap-x-3 tw-text-white/95 tw-no-underline tw-transition-colors desktop-hover:hover:tw-text-white"><div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={wave.name} picture={wave.picture} contributors={wavePictureContributors} /></div><h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight lg:tw-text-xl">{wave.name}</h1></Link> : <><div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={wave.name} picture={wave.picture} contributors={wavePictureContributors} /></div>{showDescriptionPreview ? <WaveDescriptionPopover wave={wave} align="left" ariaLabel="Show wave description" triggerClassName={`tw-group tw-flex tw-min-w-0 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left ${isCompact ? "tw-items-center" : "tw-flex-col tw-items-start"}`}>{isCompact ? <h1 className="tw-mb-0 tw-flex tw-min-w-0 tw-items-center tw-gap-x-1.5 tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95"><span className="tw-min-w-0 tw-truncate">{wave.name}</span><ChevronDownIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-300 tw-transition-colors group-hover:tw-text-white" /></h1> : <><h1 className="tw-mb-0 tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1><span className="tw-mt-0.5 tw-flex tw-w-full tw-min-w-0 tw-max-w-xl tw-items-center tw-gap-x-1.5"><span ref={descriptionPreviewRef} className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-xs tw-font-normal tw-text-iron-400 tw-transition-colors tw-duration-300 group-hover:tw-text-iron-300">{previewText}</span>{isDescriptionPreviewTruncated && <ChevronDownIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-300 tw-transition-colors group-hover:tw-text-white" />}</span></>}</WaveDescriptionPopover> : <h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>}</>;
    $[39] = directMessageProfileHref;
    $[40] = isCompact;
    $[41] = isDescriptionPreviewTruncated;
    $[42] = previewText;
    $[43] = showDescriptionPreview;
    $[44] = wave;
    $[45] = wavePictureContributors;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== t12 || $[48] !== t13) {
    t14 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-2">{t12}{t13}</div>;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  const t15 = renderLeadingActions?.(actionContext);
  let t16;
  if ($[50] !== handleWaveLinkActionClick || $[51] !== headerActionsTooltipId || $[52] !== isCompact || $[53] !== renderWaveLinkActionIcon || $[54] !== showShareAction || $[55] !== waveLinkActionIconColor || $[56] !== waveLinkActionLabel || $[57] !== waveLinkActionMode) {
    t16 = showShareAction && !isCompact && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[50] = handleWaveLinkActionClick;
    $[51] = headerActionsTooltipId;
    $[52] = isCompact;
    $[53] = renderWaveLinkActionIcon;
    $[54] = showShareAction;
    $[55] = waveLinkActionIconColor;
    $[56] = waveLinkActionLabel;
    $[57] = waveLinkActionMode;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setIsSearchOpen(true);
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== headerActionsTooltipId) {
    t19 = <button type="button" onClick={t17} aria-label="Search messages in this wave" data-tooltip-id={headerActionsTooltipId} data-tooltip-content="Search messages in this wave" className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{t18}</button>;
    $[61] = headerActionsTooltipId;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== headerActionsTooltipId || $[64] !== isCompact || $[65] !== isRightSidebarOpen || $[66] !== rightSidebarActionLabel || $[67] !== rightSidebarButtonBackgroundClassName || $[68] !== toggleRightSidebar) {
    t20 = !isCompact && <button type="button" onClick={toggleRightSidebar} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={rightSidebarActionLabel} className={`tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 ${rightSidebarButtonBackgroundClassName} tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]`} aria-label={rightSidebarActionLabel}><ChevronDoubleLeftIcon strokeWidth={2} className={`tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`} /></button>;
    $[63] = headerActionsTooltipId;
    $[64] = isCompact;
    $[65] = isRightSidebarOpen;
    $[66] = rightSidebarActionLabel;
    $[67] = rightSidebarButtonBackgroundClassName;
    $[68] = toggleRightSidebar;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  const t21 = isCompact && compactMenuItems.length > 0 && <CompactMenu aria-label="More wave actions" unstyledTrigger={true} triggerClassName="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400/60 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-950" trigger={<><span className="tw-sr-only">More wave actions</span><EllipsisHorizontalIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /></>} items={compactMenuItems} menuWidthClassName="tw-w-52" />;
  let t22;
  if ($[70] !== actionsClassName || $[71] !== t15 || $[72] !== t16 || $[73] !== t19 || $[74] !== t20 || $[75] !== t21) {
    t22 = <div className={actionsClassName}>{t15}{t16}{t19}{t20}{t21}</div>;
    $[70] = actionsClassName;
    $[71] = t15;
    $[72] = t16;
    $[73] = t19;
    $[74] = t20;
    $[75] = t21;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== headerClassName || $[78] !== t14 || $[79] !== t22) {
    t23 = <div className={headerClassName}>{t14}{t22}</div>;
    $[77] = headerClassName;
    $[78] = t14;
    $[79] = t22;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  let t24;
  if ($[81] !== headerActionsTooltipId) {
    t24 = <MyStreamActionTooltip id={headerActionsTooltipId} />;
    $[81] = headerActionsTooltipId;
    $[82] = t24;
  } else {
    t24 = $[82];
  }
  let t25;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setIsSearchOpen(false);
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  let t26;
  if ($[84] !== handleSearchSelect || $[85] !== isSearchOpen || $[86] !== wave) {
    t26 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t25} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[84] = handleSearchSelect;
    $[85] = isSearchOpen;
    $[86] = wave;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== t23 || $[89] !== t24 || $[90] !== t26) {
    t27 = <>{t23}{t24}{t26}</>;
    $[88] = t23;
    $[89] = t24;
    $[90] = t26;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  return t27;
}
function _temp(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}